/**
 * Veterinary patient sex and neuter status.
 *
 * WHY THIS FILE EXISTS: DICOM models an animal's sex as TWO independent attributes, and the app
 * used to show the user DICOM's shape instead of a vet's.
 *
 *   PatientSex          (0010,0040) VR CS, defined terms M | F | O. Carries NO neuter information.
 *   PatientSexNeutered  (0010,2203) VR CS, defined terms ALTERED | UNALTERED.
 *
 * A vet does not think in "altered". They think male, male neutered (castrated), female, female
 * spayed. Those four clinical states plus "we were not told" are what this module renders, while
 * the values that leave the browser stay the DICOM defined terms.
 *
 * THE SAFETY RULE, and it is the whole reason the "unknown" state is modelled explicitly:
 * "not stated" and "intact" are DIFFERENT clinical claims. A sender who wrote only "M" has said
 * nothing about neutering. Unknown stays unknown, is persisted as absent, and NEVER becomes
 * UNALTERED. Only an explicit intact/entire value yields UNALTERED. That is why the neuter control
 * is a three-state select and not a checkbox: a checkbox cannot express "not recorded", so it
 * would silently assert that every unrecorded patient is intact.
 *
 * THE CASING RULE: the persisted values are uppercase ALTERED / UNALTERED / absent, always, no
 * matter which label is on screen. VR CS rejects lowercase, and because the Gateway's MWL emission
 * loop has no per-item try/catch, one title-cased "Altered" row threw mid-stream and tore down an
 * entire C-FIND association (see the header of lib/schedule/dicomValues.ts for the reproduction).
 * `PERSISTED_NEUTER_VALUES` and its test are the regression guard for that bug.
 */
/**
 * The only non-empty values that may ever be persisted for (0010,2203). Uppercase, exactly as
 * PS3.3 C.7.1.4 defines them. Labels are translated; these are not.
 */
export const PERSISTED_NEUTER_VALUES = ['ALTERED', 'UNALTERED'];
/** The only non-empty values that may ever be persisted for (0010,0040) from our own UI. */
export const PERSISTED_SEX_VALUES = ['M', 'F'];
/**
 * Radix Select forbids an item with value="". These helpers move "not recorded" across that
 * boundary without letting the sentinel leak into anything we persist.
 */
export const SELECT_UNKNOWN_SENTINEL = '__';
export function toSelectValue(value) {
    return value === '' ? SELECT_UNKNOWN_SENTINEL : value;
}
export function fromSelectValue(value) {
    return value === SELECT_UNKNOWN_SENTINEL ? '' : value;
}
/**
 * Sex control: Male / Female / Unknown.
 *
 * "O" (Other) is deliberately gone. In veterinary practice nobody ever meant "other"; the picker
 * was being used as a way to say "we do not know", which conflated a stated third sex with an
 * absent one. Unknown persists as absent, so the tag is omitted rather than asserted.
 */
export const PATIENT_SEX_OPTIONS = [
    { value: 'M', labelKey: 'common:patientSex.option.male' },
    { value: 'F', labelKey: 'common:patientSex.option.female' },
    { value: '', labelKey: 'common:patientSex.option.unknown' },
];
function labelFamily(sex) {
    return sex === 'M' ? 'male' : sex === 'F' ? 'female' : 'unknown';
}
/**
 * Neuter control options, labelled for the chosen sex. Female reads "Spayed", male reads
 * "Neutered", unknown falls back to the sexless "Altered" because we genuinely cannot say which.
 * Only the LABELS vary; the values are the same DICOM defined terms in every case.
 */
export function neuterOptionsFor(sex) {
    const family = labelFamily(sex);
    return [
        { value: 'ALTERED', labelKey: `common:patientSex.neuter.${family}.altered` },
        { value: 'UNALTERED', labelKey: `common:patientSex.neuter.${family}.unaltered` },
        { value: '', labelKey: 'common:patientSex.neuter.notRecorded' },
    ];
}
/** Field label for the neuter control, also sex-dependent ("Spay status" / "Neuter status"). */
export function neuterFieldLabelKey(sex) {
    return `common:patientSex.neuterLabel.${labelFamily(sex)}`;
}
const UNKNOWN_SIGNALMENT = { sex: '', neutered: '' };
/**
 * Collapse a free-text value to a lookup key the way the backend Normalizers do: uppercase, trim,
 * collapse any run of whitespace / underscore / hyphen / slash / period to one space, then drop
 * the spaces. One step covers "M/N", "M-N", "M.N.", "MALE_NEUTERED", "maleNeutered" and
 * "  female  spayed  " without a table entry per spelling.
 */
function lookupKey(raw) {
    return raw
        .trim()
        .toUpperCase()
        .replace(/[\s_\-/.]+/g, ' ')
        .trim()
        .replace(/ /g, '');
}
/**
 * The vocabulary. Mirrors backend Shared/Integrations Normalizers.SexAndNeuter so a value that
 * round-trips through the API renders the same on the way back.
 *
 * Note which words assert a sex and which do not. SPAYED/OHE/OVH are female-only and CASTRATED/
 * GELDING are male-only, so they may assert. Bare NEUTERED/DESEXED/FIXED do NOT: reading them as
 * male is normal in North America but wrong in the UK, Ireland and Australasia, where a spayed
 * female is routinely written "Neutered". They yield an altered patient of unknown sex.
 */
const VOCABULARY = {
    // Sex only. These say NOTHING about neutering, and must never imply UNALTERED.
    M: { sex: 'M', neutered: '' },
    MALE: { sex: 'M', neutered: '' },
    '1': { sex: 'M', neutered: '' },
    F: { sex: 'F', neutered: '' },
    FEMALE: { sex: 'F', neutered: '' },
    '2': { sex: 'F', neutered: '' },
    // Stated unknown. Indistinguishable from absent for our purposes, and that is correct.
    U: UNKNOWN_SIGNALMENT,
    UNKNOWN: UNKNOWN_SIGNALMENT,
    O: UNKNOWN_SIGNALMENT,
    OTHER: UNKNOWN_SIGNALMENT,
    NA: UNKNOWN_SIGNALMENT,
    UNSPECIFIED: UNKNOWN_SIGNALMENT,
    // Male, altered.
    MN: { sex: 'M', neutered: 'ALTERED' },
    NM: { sex: 'M', neutered: 'ALTERED' },
    MALENEUTERED: { sex: 'M', neutered: 'ALTERED' },
    NEUTEREDMALE: { sex: 'M', neutered: 'ALTERED' },
    CASTRATED: { sex: 'M', neutered: 'ALTERED' },
    CASTRATEDMALE: { sex: 'M', neutered: 'ALTERED' },
    MALECASTRATED: { sex: 'M', neutered: 'ALTERED' },
    GELDING: { sex: 'M', neutered: 'ALTERED' },
    GELDED: { sex: 'M', neutered: 'ALTERED' },
    // Female, altered.
    FS: { sex: 'F', neutered: 'ALTERED' },
    SF: { sex: 'F', neutered: 'ALTERED' },
    FN: { sex: 'F', neutered: 'ALTERED' },
    SPAYED: { sex: 'F', neutered: 'ALTERED' },
    SPEYED: { sex: 'F', neutered: 'ALTERED' },
    SPAYEDFEMALE: { sex: 'F', neutered: 'ALTERED' },
    FEMALESPAYED: { sex: 'F', neutered: 'ALTERED' },
    FEMALESPEYED: { sex: 'F', neutered: 'ALTERED' },
    FEMALENEUTERED: { sex: 'F', neutered: 'ALTERED' },
    OHE: { sex: 'F', neutered: 'ALTERED' },
    OVH: { sex: 'F', neutered: 'ALTERED' },
    // Male, explicitly intact. Only an explicit word gets here.
    MI: { sex: 'M', neutered: 'UNALTERED' },
    IM: { sex: 'M', neutered: 'UNALTERED' },
    MALEINTACT: { sex: 'M', neutered: 'UNALTERED' },
    INTACTMALE: { sex: 'M', neutered: 'UNALTERED' },
    MALEENTIRE: { sex: 'M', neutered: 'UNALTERED' },
    ENTIREMALE: { sex: 'M', neutered: 'UNALTERED' },
    STUD: { sex: 'M', neutered: 'UNALTERED' },
    STALLION: { sex: 'M', neutered: 'UNALTERED' },
    // Female, explicitly intact.
    FI: { sex: 'F', neutered: 'UNALTERED' },
    IF: { sex: 'F', neutered: 'UNALTERED' },
    FEMALEINTACT: { sex: 'F', neutered: 'UNALTERED' },
    INTACTFEMALE: { sex: 'F', neutered: 'UNALTERED' },
    FEMALEENTIRE: { sex: 'F', neutered: 'UNALTERED' },
    ENTIREFEMALE: { sex: 'F', neutered: 'UNALTERED' },
    MARE: { sex: 'F', neutered: 'UNALTERED' },
    // Sexless neuter words. Altered is asserted, sex is NOT.
    NEUTERED: { sex: '', neutered: 'ALTERED' },
    DESEXED: { sex: '', neutered: 'ALTERED' },
    ALTERED: { sex: '', neutered: 'ALTERED' },
    STERILIZED: { sex: '', neutered: 'ALTERED' },
    STERILISED: { sex: '', neutered: 'ALTERED' },
    FIXED: { sex: '', neutered: 'ALTERED' },
    // Sexless intact words.
    INTACT: { sex: '', neutered: 'UNALTERED' },
    ENTIRE: { sex: '', neutered: 'UNALTERED' },
    UNALTERED: { sex: '', neutered: 'UNALTERED' },
};
function lookup(raw) {
    if (raw == null)
        return UNKNOWN_SIGNALMENT;
    const key = lookupKey(String(raw));
    if (key === '')
        return UNKNOWN_SIGNALMENT;
    return VOCABULARY[key] ?? UNKNOWN_SIGNALMENT;
}
/**
 * Combine whatever the two columns hold into one clinical picture.
 *
 * The dedicated neuter field wins when it says something, because it is the field a user
 * deliberately set. A combined code in the sex field ("MN", "FS") still contributes both facts,
 * which matters because the two vocabularies genuinely coexist in schedule_item.patient_sex today:
 * order ingestion writes DICOM M/F/O while the canonical report payload documents M/F/MN/FS.
 * A sex-bearing word in the NEUTER field ("Spayed") also contributes its sex, so the PIMS habit of
 * putting the whole signalment in one box does not lose half of it.
 *
 * Anything unrecognised degrades to unknown. It never throws and it never guesses UNALTERED.
 */
export function parseSignalment(rawSex, rawNeutered) {
    const fromSex = lookup(rawSex);
    const fromNeuter = lookup(rawNeutered);
    return {
        sex: fromSex.sex || fromNeuter.sex,
        neutered: fromNeuter.neutered || fromSex.neutered,
    };
}
/** Just the sex half, for seeding a sex control from a possibly-combined stored value. */
export function normalizeSexValue(raw) {
    return lookup(raw).sex;
}
/** Just the neuter half, for seeding the neuter control. */
export function normalizeNeuterValue(raw) {
    return lookup(raw).neutered;
}
/**
 * The compact signalment a vet reads at a glance: FS, MN, F, M, or empty.
 *
 * Deliberate degradations, both of which match what the backend wire codes do:
 *  - explicitly intact reads as plain "F" / "M". There is no conventional abbreviation for it and
 *    no wire code either, so the long form carries that fact instead.
 *  - unknown sex reads as empty even when the animal is known to be altered, because "S" or "N"
 *    on its own is not a signalment anybody would recognise. Again, the long form carries it.
 */
export function formatSignalment(sex, neutered) {
    if (sex === 'F')
        return neutered === 'ALTERED' ? 'FS' : 'F';
    if (sex === 'M')
        return neutered === 'ALTERED' ? 'MN' : 'M';
    return '';
}
/** Convenience for display sites holding the two raw column values. */
export function formatSignalmentFromRaw(rawSex, rawNeutered) {
    const { sex, neutered } = parseSignalment(rawSex, rawNeutered);
    return formatSignalment(sex, neutered);
}
/**
 * Translation key for the spelled-out form used in tooltips and accessible names. Split out from
 * the formatter so the mapping is testable without a translator.
 */
export function signalmentLongKey(sex, neutered) {
    if (sex === 'F') {
        if (neutered === 'ALTERED')
            return 'common:patientSex.long.femaleSpayed';
        if (neutered === 'UNALTERED')
            return 'common:patientSex.long.femaleIntact';
        return 'common:patientSex.long.female';
    }
    if (sex === 'M') {
        if (neutered === 'ALTERED')
            return 'common:patientSex.long.maleNeutered';
        if (neutered === 'UNALTERED')
            return 'common:patientSex.long.maleIntact';
        return 'common:patientSex.long.male';
    }
    if (neutered === 'ALTERED')
        return 'common:patientSex.long.unknownAltered';
    if (neutered === 'UNALTERED')
        return 'common:patientSex.long.unknownIntact';
    return 'common:patientSex.long.unknown';
}
/** Spelled-out form: "Female, spayed", "Male, intact", "Not recorded". */
export function formatSignalmentLong(sex, neutered, t) {
    return t(signalmentLongKey(sex, neutered));
}
/** Convenience wrapper for display sites holding the two raw column values. */
export function formatSignalmentLongFromRaw(rawSex, rawNeutered, t) {
    const { sex, neutered } = parseSignalment(rawSex, rawNeutered);
    return formatSignalmentLong(sex, neutered, t);
}
